import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, CloudDownloadOutlined, RocketLaunchOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SurveyTable from "../../components/SurveyTable";
import { usePageContext } from "../../context/PageContext";
import { can, toSet, useEffectivePermissions } from "../../hooks/useEffectivePermissions";
import { useSurveyDetails, useSurveyTemplates, useSurveys } from "../../hooks/useSurveys";
import SurveyBuilderDialog from "./SurveyBuilderDialog";

const SummaryCard = ({ label, value, helper }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const toSafeSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const Surveys = () => {
  const { currentPage, setCurrentPage } = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { data: permissionData } = useEffectivePermissions();
  const permissionSet = toSet(permissionData?.scopes);

  const canCreate = can(permissionSet, "surveys", "create");
  const canUpdate = can(permissionSet, "surveys", "update");
  const canDelete = can(permissionSet, "surveys", "delete");

  const [tab, setTab] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [builderState, setBuilderState] = useState({ open: false, surveyId: null, survey: null });

  const {
    data,
    error,
    refetch,
    isLoading,
    saveMutation,
    deleteMutation,
    createFromTemplateMutation,
    syncLegacyMutation,
  } = useSurveys(currentPage, pageSize, search, statusFilter);

  const { data: templates = [] } = useSurveyTemplates();
  const { data: editingSurvey } = useSurveyDetails(builderState.surveyId, builderState.open);

  const surveys = data?.surveys || [];
  const totalPublished = surveys.filter((survey) => survey.status === "published").length;
  const totalCompleted = surveys.reduce(
    (total, survey) => total + (survey.summary?.completedCount || 0),
    0
  );
  const avgScore = useMemo(() => {
    const numericScores = surveys
      .map((survey) => survey.summary?.scoreSummary?.mainValue)
      .filter((value) => value != null);
    if (!numericScores.length) return "—";
    return (numericScores.reduce((sum, value) => sum + Number(value), 0) / numericScores.length).toFixed(1);
  }, [surveys]);
  const executiveOverview = useMemo(() => {
    const startedTotal = surveys.reduce(
      (total, survey) => total + Number(survey.summary?.startedCount || 0),
      0
    );
    const completedTotal = surveys.reduce(
      (total, survey) => total + Number(survey.summary?.completedCount || 0),
      0
    );
    const completionRate =
      startedTotal > 0 ? Number(((completedTotal / startedTotal) * 100).toFixed(1)) : null;

    const rankedByCompletion = surveys
      .map((survey) => ({
        ...survey,
        completionRate:
          survey.summary?.completionRate ??
          (Number(survey.summary?.startedCount || 0) > 0
            ? Number(
                (
                  (Number(survey.summary?.completedCount || 0) /
                    Number(survey.summary?.startedCount || 0)) *
                  100
                ).toFixed(1)
              )
            : null),
      }))
      .filter((survey) => Number(survey.summary?.startedCount || 0) >= 5 && survey.completionRate != null)
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 4);

    const publishedWithoutResponses = surveys
      .filter(
        (survey) =>
          survey.status === "published" && Number(survey.summary?.completedCount || 0) === 0
      )
      .slice(0, 4);

    return {
      startedTotal,
      completedTotal,
      completionRate,
      rankedByCompletion,
      publishedWithoutResponses,
    };
  }, [surveys]);

  const activeBuilderSurvey = builderState.surveyId ? editingSurvey : builderState.survey;

  React.useEffect(() => {
    if (error) {
      setErrorMessage(error.message || "Não foi possível carregar as pesquisas.");
    }
  }, [error]);

  const handleSearch = (term) => {
    setCurrentPage(1);
    setSearch(term);
  };

  const handleDeleteSurvey = (surveyId) => {
    setConfirmDelete(surveyId);
    setErrorMessage(null);
  };

  const confirmDeleteSurvey = () => {
    if (!confirmDelete) return;
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (error) => {
        setErrorMessage(error?.message || "Erro ao excluir a pesquisa.");
      },
    });
  };

  const handleSaveSurvey = (payload) => {
    saveMutation.mutate(payload, {
      onSuccess: () => {
        setBuilderState({ open: false, surveyId: null, survey: null });
        refetch();
      },
      onError: (error) => {
        setErrorMessage(error?.message || "Erro ao salvar a pesquisa.");
      },
    });
  };

  const handleCreateFromTemplate = (template) => {
    createFromTemplateMutation.mutate(
      {
        templateId: template.id,
        title: template.name,
        description: template.description,
        metricType: template.metricType,
        audienceType: template.audienceType,
        estimatedMinutes: template.estimatedMinutes,
        questions: template.questions,
        status: "draft",
      },
      {
        onSuccess: (response) => {
          const createdSurvey = response?.data || null;
          if (createdSurvey?.id) {
            setBuilderState({ open: true, surveyId: createdSurvey.id, survey: null });
            setTab(2);
          }
          refetch();
        },
        onError: (error) => {
          setErrorMessage(error?.message || "Erro ao criar pesquisa a partir do template.");
        },
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    const results = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const failed = results.filter((result) => result.status === "rejected");
    if (failed.length) {
      setErrorMessage(
        failed.length === ids.length
          ? "Nenhuma pesquisa selecionada foi excluída."
          : `${failed.length} pesquisa(s) não puderam ser excluídas.`
      );
    }
    refetch();
  };

  const handleOpenPublic = (survey) => {
    const company = localStorage.getItem("company");
    if (!company) {
      setErrorMessage("Selecione um workspace antes de abrir o link público.");
      return;
    }
    const explicitSlug = String(survey?.slug || "").trim();
    const slug =
      explicitSlug && explicitSlug !== "undefined" && explicitSlug !== "null"
        ? explicitSlug
        : survey?.id
        ? `survey-${survey.id}`
        : toSafeSlug(survey?.title || survey?.titulo);
    if (!slug) {
      setErrorMessage("Esta pesquisa ainda não possui slug público. Salve/atualize a pesquisa para gerar o link.");
      return;
    }
    window.open(
      `${window.location.origin}/public/surveys/${slug}?company=${encodeURIComponent(company)}`,
      "_blank"
    );
  };

  const rightActions = (
    <Stack direction={isMobile ? "column" : "row"} spacing={1}>
      <Button
        variant="outlined"
        startIcon={<CloudDownloadOutlined />}
        onClick={() =>
          syncLegacyMutation.mutate(
            { includeResponses: true, limit: 1000 },
            {
              onSuccess: () => refetch(),
              onError: (error) =>
                setErrorMessage(error?.message || "Erro ao importar pesquisas legadas."),
            }
          )
        }
      >
        Importar legado
      </Button>
      {canCreate && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setBuilderState({ open: true, surveyId: null, survey: {} })}
        >
          Nova pesquisa
        </Button>
      )}
    </Stack>
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Pesquisas</Typography>
          <Typography color="text.secondary">
            Templates prontos, importação do legado, jornada pública e analytics executiva.
          </Typography>
        </Box>
        {rightActions}
      </Stack>

      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <SummaryCard label="Pesquisas mapeadas" value={surveys.length} helper="Página atual" />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard label="Publicadas" value={totalPublished} helper="Prontas para responder" />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard label="Respostas concluídas" value={totalCompleted} helper="Amostra carregada" />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard label="Score médio" value={avgScore} helper="Média do score principal" />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Visão executiva" />
        <Tab label={`Templates (${templates.length})`} />
        <Tab label={`Gestão (${data?.pagination?.total || 0})`} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6">Resumo operacional</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Sessões iniciadas"
                      value={executiveOverview.startedTotal}
                      helper="No recorte atual"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Respostas concluídas"
                      value={executiveOverview.completedTotal}
                      helper="No recorte atual"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Taxa geral de conclusão"
                      value={
                        executiveOverview.completionRate == null
                          ? "—"
                          : `${executiveOverview.completionRate}%`
                      }
                      helper="Concluídas / iniciadas"
                    />
                  </Grid>
                </Grid>
                <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ mt: 2 }}>
                  <Button variant="outlined" onClick={() => setTab(1)}>
                    Ver templates
                  </Button>
                  <Button variant="contained" onClick={() => setTab(2)}>
                    Abrir gestão de pesquisas
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6">Destaques e alertas</Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {executiveOverview.rankedByCompletion.map((survey) => (
                    <Box key={`top-${survey.id}`}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2">
                            {survey.title || survey.titulo || `Pesquisa #${survey.id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {survey.summary?.completedCount || 0} respostas concluídas
                          </Typography>
                        </Box>
                        <Chip label={`${survey.completionRate}%`} size="small" color="success" />
                      </Stack>
                    </Box>
                  ))}
                  {executiveOverview.publishedWithoutResponses.map((survey) => (
                    <Box key={`alert-${survey.id}`}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2">
                            {survey.title || survey.titulo || `Pesquisa #${survey.id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Publicada, sem respostas concluídas
                          </Typography>
                        </Box>
                        <Chip label="Atenção" size="small" color="warning" />
                      </Stack>
                    </Box>
                  ))}
                  {!executiveOverview.rankedByCompletion.length &&
                  !executiveOverview.publishedWithoutResponses.length ? (
                    <Typography variant="body2" color="text.secondary">
                      Sem dados suficientes ainda. Publique pesquisas e acompanhe as primeiras
                      respostas para ver os destaques.
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{template.name}</Typography>
                    <Chip label={template.metricType.toUpperCase()} color="primary" size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, minHeight: 48 }}>
                    {template.description}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={`${template.questions.length} perguntas`} />
                    <Chip size="small" variant="outlined" label={`${template.estimatedMinutes} min`} />
                    <Chip size="small" variant="outlined" label={template.audienceType} />
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {(template.tags || []).map((tag) => (
                      <Chip key={tag} size="small" label={tag} />
                    ))}
                  </Stack>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<RocketLaunchOutlined />}
                    sx={{ mt: 3 }}
                    disabled={!canCreate}
                    onClick={() => handleCreateFromTemplate(template)}
                  >
                    Usar template
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {tab === 2 && (
        <SurveyTable
          surveys={surveys}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={(data?.pagination?.page || currentPage) - 1}
          onPageChange={(page) => setCurrentPage(page + 1)}
          onPageSizeChange={setPageSize}
          onSearch={handleSearch}
          isLoading={isLoading || saveMutation.isLoading || syncLegacyMutation.isLoading}
          onEditSurvey={(survey) =>
            setBuilderState({ open: true, surveyId: survey.id, survey })
          }
          onDeleteSurvey={handleDeleteSurvey}
          onDeleteSelected={handleDeleteSelected}
          onViewAnalytics={(survey) => navigate(`/surveys/${survey.id}/analytics`)}
          onOpenPublic={handleOpenPublic}
          canEdit={canUpdate}
          canDelete={canDelete}
        />
      )}

      <SurveyBuilderDialog
        open={builderState.open}
        templates={templates}
        initialSurvey={activeBuilderSurvey}
        loading={saveMutation.isLoading}
        onClose={() => setBuilderState({ open: false, surveyId: null, survey: null })}
        onSubmit={handleSaveSurvey}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Excluir pesquisa</DialogTitle>
        <DialogContent>
          <Typography>
            Esta ação faz exclusão lógica da pesquisa. Respostas históricas continuam preservadas
            para auditoria.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button color="error" onClick={confirmDeleteSurvey}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Surveys;
